import { isSceneEffectType } from '../types/sceneEffects';
import { useEditorStore, useMovieStore, useTimelineStore } from '../stores';
import { resolveStateTarget } from './resolveStateTarget';
import { executeAiJobs } from './executeAiJobs';
import { mapLibraryObjectResponses } from './mapLibraryObjectResponses';
const LAYOUT_PROPERTIES = new Set([
    'x',
    'y',
    'left',
    'top',
    'width',
    'height',
    'rotation',
    'opacity',
]);
function asRecord(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    return value;
}
function readNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}
function readObjectId(record) {
    return readNumber(record.objectId ?? record.sceneObjectId ?? record.id);
}
function normalizePropertyName(property) {
    if (property === 'left')
        return 'x';
    if (property === 'top')
        return 'y';
    return property;
}
function buildPatchFromRecord(record) {
    const patch = {};
    const property = record.property;
    const value = record.value;
    if (typeof property === 'string' && value !== undefined) {
        const normalized = normalizePropertyName(property);
        if (!LAYOUT_PROPERTIES.has(normalized) && !LAYOUT_PROPERTIES.has(property)) {
            return null;
        }
        const numeric = readNumber(value);
        if (numeric == null)
            return null;
        patch[normalized] = numeric;
        if (normalized === 'x')
            patch.left = numeric;
        if (normalized === 'y')
            patch.top = numeric;
        return patch;
    }
    const properties = asRecord(record.properties);
    if (properties) {
        for (const [key, rawValue] of Object.entries(properties)) {
            const normalized = normalizePropertyName(key);
            const numeric = readNumber(rawValue);
            if (numeric == null)
                continue;
            patch[normalized] = numeric;
            if (normalized === 'x')
                patch.left = numeric;
            if (normalized === 'y')
                patch.top = numeric;
        }
    }
    return Object.keys(patch).length > 0 ? patch : null;
}
function buildEffectParamsFromRecord(record) {
    const params = asRecord(record.params) ?? {};
    const merged = { ...params };
    for (const key of ['intensity', 'particleSize', 'speed', 'color']) {
        if (record[key] !== undefined)
            merged[key] = record[key];
    }
    return merged;
}
async function applySceneEffectPayload(record, kind, index, deps) {
    const effectRaw = record.effect;
    const actionRaw = record.action;
    if (typeof effectRaw !== 'string' || typeof actionRaw !== 'string')
        return null;
    if (!deps.applyEffectPayload) {
        return {
            kind,
            index,
            status: 'skipped',
            detail: 'Scene effect executor not available',
        };
    }
    const effectType = effectRaw.trim().toLowerCase();
    if (!isSceneEffectType(effectType)) {
        return {
            kind,
            index,
            status: 'skipped',
            detail: `Unsupported effect type: ${effectRaw}`,
        };
    }
    const connected = await ensureSignalRConnected(deps);
    if (!connected) {
        return { kind, index, status: 'failed', detail: 'SignalR not connected' };
    }
    try {
        const params = buildEffectParamsFromRecord(record);
        await deps.applyEffectPayload(effectType, actionRaw, params);
        return {
            kind,
            index,
            status: 'applied',
            detail: `${actionRaw} effect ${effectType}`,
        };
    }
    catch (error) {
        console.error('[aiActionExecutor] effect payload failed', error);
        return {
            kind,
            index,
            status: 'failed',
            detail: error instanceof Error ? error.message : 'Effect payload failed',
        };
    }
}
async function ensureSignalRConnected(deps) {
    if (deps.signalRStatus === 'Connected')
        return true;
    try {
        await deps.connect();
        return true;
    }
    catch (error) {
        console.error('[aiActionExecutor] SignalR connect failed', error);
        return false;
    }
}
async function applyObjectPatch(objectId, patch, deps) {
    const movie = useMovieStore.getState();
    const timeline = useTimelineStore.getState();
    const editor = useEditorStore.getState();
    const sceneObject = movie.currentScene?.objects?.find((obj) => String(obj.id) === String(objectId));
    if (!sceneObject) {
        return {
            kind: 'payload',
            index: 0,
            status: 'failed',
            detail: `Scene object ${objectId} not found`,
        };
    }
    const layerIdRaw = sceneObject.layerId ?? movie.currentLayer?.id;
    const layerIdNum = readNumber(layerIdRaw);
    if (layerIdNum == null) {
        return {
            kind: 'payload',
            index: 0,
            status: 'failed',
            detail: `Layer for object ${objectId} not found`,
        };
    }
    const layerIdStr = String(layerIdNum);
    const layerKeyframes = timeline.keyframes[layerIdStr] ?? [];
    const target = resolveStateTarget({
        layerId: layerIdStr,
        currentFrame: editor.currentFrame,
        layerKeyframes,
        selectedKeyframe: null,
    });
    const keyFrameId = target.kind === 'keyframe' ? (target.keyframeId ?? 0) : 0;
    const nextPose = {
        x: patch.x ?? sceneObject.x,
        y: patch.y ?? sceneObject.y,
        width: patch.width ?? sceneObject.width,
        height: patch.height ?? sceneObject.height,
        rotation: patch.rotation ?? sceneObject.rotation,
        opacity: patch.opacity ?? sceneObject.opacity,
    };
    movie.updateSceneObject(String(objectId), nextPose);
    const connected = await ensureSignalRConnected(deps);
    if (!connected) {
        return {
            kind: 'payload',
            index: 0,
            status: 'failed',
            detail: 'SignalR not connected',
        };
    }
    try {
        await deps.sceneObjectPatchStateProperties({
            sceneObjectId: objectId,
            keyFrameId,
            patch: JSON.stringify(patch),
        });
        return {
            kind: 'payload',
            index: 0,
            status: 'applied',
            detail: `Patched object ${objectId} at keyFrame ${keyFrameId}`,
        };
    }
    catch (error) {
        console.error('[aiActionExecutor] patch failed', error);
        return {
            kind: 'payload',
            index: 0,
            status: 'failed',
            detail: error instanceof Error ? error.message : 'Patch failed',
        };
    }
}
async function executeStructuredItem(item, kind, index, deps) {
    const record = asRecord(item);
    if (!record) {
        return { kind, index, status: 'skipped', detail: 'Not a JSON object' };
    }
    const actionName = typeof record.action === 'string' ? record.action.toLowerCase() : null;
    if (actionName === 'createmovie' || actionName === 'create_movie') {
        return {
            kind,
            index,
            status: 'skipped',
            detail: 'createMovie handled by client heuristic before chat send',
        };
    }
    if (typeof record.effect === 'string' && typeof record.action === 'string') {
        const effectResult = await applySceneEffectPayload(record, kind, index, deps);
        if (effectResult)
            return effectResult;
    }
    if (actionName === 'patchobject' ||
        actionName === 'updateobject' ||
        actionName === 'patch_object' ||
        record.objectId != null ||
        record.sceneObjectId != null) {
        const objectId = readObjectId(record);
        const patch = buildPatchFromRecord(record);
        if (objectId == null || !patch) {
            return { kind, index, status: 'skipped', detail: 'Missing objectId or patch fields' };
        }
        const result = await applyObjectPatch(objectId, patch, deps);
        return { ...result, kind, index };
    }
    if (kind === 'job') {
        return { kind, index, status: 'skipped', detail: 'Image jobs are executed in batch' };
    }
    return { kind, index, status: 'skipped', detail: 'Unsupported structured item' };
}
/** AI yanıtındaki payloads, actions ve jobs bloklarını uygular veya kuyruğa alır. */
export async function executeAiAssistantActions(parsed, deps) {
    const results = [];
    for (let index = 0; index < parsed.payloads.length; index += 1) {
        results.push(await executeStructuredItem(parsed.payloads[index], 'payload', index, deps));
    }
    for (let index = 0; index < parsed.actions.length; index += 1) {
        results.push(await executeStructuredItem(parsed.actions[index], 'action', index, deps));
    }
    if (parsed.jobs.length > 0) {
        results.push(...(await executeImageJobs(parsed.jobs, deps)));
    }
    if (results.length > 0) {
        console.info('[aiActionExecutor] results', results);
    }
    return results;
}
async function executeImageJobs(jobs, deps) {
    if (!deps.movieId) {
        return jobs.map((_, index) => ({
            kind: 'job',
            index,
            status: 'failed',
            detail: 'Movie context missing for image job',
        }));
    }
    const jobRecords = jobs
        .map((job) => asRecord(job))
        .filter((job) => job != null);
    if (jobRecords.length === 0) {
        return jobs.map((_, index) => ({
            kind: 'job',
            index,
            status: 'skipped',
            detail: 'Not a JSON object',
        }));
    }
    try {
        const response = await executeAiJobs(deps.movieId, {
            jobs: jobRecords,
            sceneId: deps.sceneId ?? null,
            currentFrame: deps.currentFrame ?? null,
        });
        const results = jobRecords.map((_, index) => {
            const result = response.results[index];
            if (!result) {
                return {
                    kind: 'job',
                    index,
                    status: 'failed',
                    detail: 'No job result returned from server',
                };
            }
            if (result.status === 'applied' &&
                result.libraryObject &&
                deps.onLibraryItemCreated) {
                const [item] = mapLibraryObjectResponses([result.libraryObject], deps.userId, deps.movieId);
                if (item) {
                    deps.onLibraryItemCreated({
                        id: item.id,
                        name: item.name,
                        type: 'image',
                        src: item.src,
                        thumbnail: item.thumbnail ?? item.src,
                    });
                }
            }
            const status = result.status === 'applied'
                ? 'applied'
                : result.status === 'skipped'
                    ? 'skipped'
                    : 'failed';
            return {
                kind: 'job',
                index,
                status,
                detail: result.detail ?? `Worker ${result.worker}: ${result.status}`,
            };
        });
        if (results.some((entry) => entry.status === 'applied') && deps.refreshLibrary) {
            await deps.refreshLibrary();
        }
        return results;
    }
    catch (error) {
        console.error('[aiActionExecutor] image jobs failed', error);
        const detail = error instanceof Error ? error.message : 'Image job execution failed';
        return jobRecords.map((_, index) => ({
            kind: 'job',
            index,
            status: 'failed',
            detail,
        }));
    }
}
